import type { BrandTokens } from '../types/theme'

/** Scale-backed brand keys. Content overrides are not scaled. */
export type BrandScaleKey =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'background'
  | 'surface'
  | 'error'
  | 'fontColor'
  | 'sidebar'
  | 'border'
  | 'success'
  | 'warning'
  | 'neutral'
  | 'info'

/** Cookie key for guest / SSR theme preference. */
export const THEME_COOKIE_KEY = 'cp-theme-preference'
/** Cookie max-age: 1 year. */
export const THEME_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365
export const COMMON_DARK_SIDEBAR_COLOR = '#242526'

export const TOKEN_CSS_MAP: Record<BrandScaleKey, string> = {
  primary: '--cp-primary',
  secondary: '--cp-secondary',
  tertiary: '--cp-tertiary',
  background: '--cp-background',
  surface: '--cp-surface',
  error: '--cp-error',
  fontColor: '--cp-font',
  sidebar: '--cp-sidebar',
  border: '--cp-border',
  success: '--cp-success',
  warning: '--cp-warning',
  neutral: '--cp-neutral',
  info: '--cp-info',
}

export const BRAND_CSS_MAP: Record<BrandScaleKey, string> = {
  primary: '--cp-brand-primary',
  secondary: '--cp-brand-secondary',
  tertiary: '--cp-brand-tertiary',
  background: '--cp-brand-background',
  surface: '--cp-brand-surface',
  error: '--cp-brand-error',
  fontColor: '--cp-brand-font',
  sidebar: '--cp-brand-sidebar',
  border: '--cp-brand-border',
  success: '--cp-brand-success',
  warning: '--cp-brand-warning',
  neutral: '--cp-brand-neutral',
  info: '--cp-brand-info',
}

export const TOKEN_SCALE_NAMES: Record<BrandScaleKey, string> = {
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
  background: 'background',
  surface: 'surface',
  error: 'error',
  fontColor: 'font',
  sidebar: 'sidebar',
  border: 'border',
  success: 'success',
  warning: 'warning',
  neutral: 'neutral',
  info: 'info',
}

/**
 * HighRise (@platform-ui/highrise) defines its own root-level scales like
 * `--primary-50 … --primary-900` that every HL component reads internally
 * (HLButton, HLCheckbox, HLRadio, HLTabs, HLCalendar, etc.). Those defaults
 * are always blue regardless of the active brand theme, which is why
 * patching per-CP component `:style` overrides never scales.
 *
 * We bridge HighRise's prefixes to our `--cp-*` scales so the entire
 * HL component tree picks up the active brand primary automatically.
 *
 * Only `primary` and `error` are bridged because those are the only two
 * prefixes HighRise consumes at component level (focus rings, borders,
 * active states, danger variants). Other tokens (`background`, `surface`,
 * `tertiary`, `sidebar`, `fontColor`) are applied directly via our own
 * `--cp-*` variables in component styles and do not need a HighRise bridge.
 */
export const HIGHRISE_BRIDGE_MAP: Partial<Record<keyof BrandTokens, string>> = {
  primary: '--primary',
  error: '--error',
}

export const DEFAULT_FONT_SCALE = {
  50: '#f3f4f6',
  100: '#f2f4f7',
  200: '#d1d5db',
  300: '#9ca3af',
  400: '#98a2b3',
  500: '#667085',
  600: '#475467',
  700: '#344054',
  800: '#1d2939',
  900: '#101828',
} as const

/** Brand keys that drive `--cp-text-on-*` contrast bindings. */
export const ON_COLOR_BINDINGS = [
  { key: 'primary', cssVar: '--cp-text-on-primary', scaleVar: '--cp-primary-600' },
  { key: 'secondary', cssVar: '--cp-text-on-secondary', scaleVar: '--cp-secondary-600' },
  { key: 'tertiary', cssVar: '--cp-text-on-tertiary', scaleVar: '--cp-tertiary-600' },
  { key: 'error', cssVar: '--cp-text-on-error', scaleVar: '--cp-error-600' },
  { key: 'sidebar', cssVar: '--cp-text-on-sidebar', scaleVar: '--cp-sidebar-500' },
] as const
